import { format } from "date-fns";
import { html, nothing } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { translate } from "../../../../localization";
import "../../../add/handle-busy/is-grouping-busy/component";
import { hasItemDivider, shouldShowRole } from "../../../domain/business-rules";
import {
  IActivityHistoryDiagnostic,
  IActivityHistoryGroup,
  IActivityHistoryItem,
  IActivityHistoryItemWithComponent,
} from "../../../domain/model";
import {
  ActivityHistoryEntry,
  mergeHistoryItemsAndSubgroups,
} from "../../merge-history-items-and-subgroups/merge-history-items-and-subgroups";
import { ActivityHistoryTimeline } from "./activity-history-timeline";

export const template = (props: ActivityHistoryTimeline) => {
  return html`
    <div class="container">
      <activity-history-full-data-error></activity-history-full-data-error>
      <activity-history-partial-data-error></activity-history-partial-data-error>

      ${
        props.historyGroups?.length > 0
          ? html`
            <div class="virtual-container">
              <div class="virtual-viewport" @scroll=${props._onScroll}>
                <div class="virtual-content" style="height: ${props._totalHeight}px;">
                  <div class="virtual-items" style="transform: translateY(${props._containerOffset}px)">
                    ${repeat(
                      props._visibleRows,
                      (row) => row.group?.items?.[0]?.id || row.group?.subGroups?.[0]?.items?.[0]?.id,
                      ({ index, group: itemGroup }) => {
                        return html`
                          <div class="visit"
                               data-index=${index}
                               ${ref(props._measureElement)}
                               data-date=${ifDefined(itemGroup?.items[0]?.date || itemGroup?.subGroups[0]?.items[0]?.date)}>
                            ${visitHeaderTemplate(props, itemGroup?.items[0] || itemGroup?.subGroups[0]?.items[0], itemGroup)}
                            <div class="visit__items">
                              ${repeat(
                                mergeHistoryItemsAndSubgroups(itemGroup),
                                (entry) => (entry.type === "item" ? entry.item.id : entry.subGroup.id),
                                (entry: ActivityHistoryEntry) => {
                                  if (entry.type === "item") {
                                    const item = entry.item;
                                    return html`
                                      <div
                                        class="item"
                                        ?has-divider=${hasItemDivider(item, itemGroup?.items as IActivityHistoryItemWithComponent[])}
                                      >
                                        ${item.component}
                                      </div>
                                    `;
                                  }
                                  const subGroup = entry.subGroup;
                                  return html`
                                    <div class="diagnostics">
                                      ${diagnosticHeaderTemplate(subGroup?.items[0])}
                                      <div class="diagnostics__items">
                                        ${repeat(
                                          subGroup?.items,
                                          (item) => item.id,
                                          (item: IActivityHistoryItemWithComponent) => html`
                                            <div class="item"
                                                 ?has-divider=${hasItemDivider(item, subGroup?.items as IActivityHistoryItemWithComponent[])}>
                                              ${item.component}
                                            </div>
                                          `,
                                        )}
                                      </div>
                                    </div>
                                  `;
                                },
                              )}
                            </div>
                          </div>
                        `;
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>
          `
          : noGroupsTemplate(props)
      }
    </div>
  `;
};

const noGroupsTemplate = (props: ActivityHistoryTimeline) => html`
${
  props.searchString?.length > 1
    ? html`
          <dss-user-feedback
            class="feedback"
            status="empty"
            title=${translate("noResults")}>
          </dss-user-feedback>
        `
    : props.isAnyCollectionBusy
      ? html`<activity-history-is-grouping-busy></activity-history-is-grouping-busy>`
      : props._canShowNoDataMessage
        ? html`<dss-user-feedback
              class="feedback"
              status="empty"
              title=${translate("noData.title")}
              description=${translate("noData.description")}>
            </dss-user-feedback>`
        : nothing
}`;

const visitHeaderTemplate = (
  props: ActivityHistoryTimeline,
  item: IActivityHistoryItem | undefined,
  itemGroup?: IActivityHistoryGroup,
) => {
  if (!item) return nothing;
  return html`
  <div class="visit__header">
    <div class="title">
      <div class="title-date">
        <div>${format(new Date(item.date), "dd/MM/yyyy")}</div>
      </div>
      <div class="title-description">
        <div>
          ${renderProfessionalValues(props, item, itemGroup)}
        </div>
      </div>
    </div>
  </div>
  `;
};

// Buscar el primer servei informat dins del grup
const findServiceInGroup = (itemGroup?: IActivityHistoryGroup): { id?: string; description?: string } | undefined => {
  if (!itemGroup) return undefined;

  // Buscar en items
  for (const item of itemGroup.items || []) {
    if (item.service?.id) return item.service;
  }

  // Buscar en subGroups
  for (const subGroup of itemGroup.subGroups || []) {
    for (const item of subGroup.items || []) {
      if (item.service?.id) return item.service;
    }
  }

  return undefined;
};

const renderProfessionalValues = (
  props: ActivityHistoryTimeline,
  item: IActivityHistoryItem,
  itemGroup?: IActivityHistoryGroup,
) => {
  if (!item.professional) return nothing;
  const showRole = shouldShowRole(item);

  const values: Array<any> = [];

  values.push(props.highlighted(item.professional.name));

  if (showRole && item.professional?.role?.description) {
    values.push(props.highlighted(item.professional.role.description));
  }

  if (item.professional?.speciality?.description) {
    values.push(props.highlighted(item.professional.speciality.description));
  }

  if (item.up?.description) {
    values.push(props.highlighted(item.up.description));
  }

  // Buscar el servei: primer de l'item, si no del grup
  const service = item.service?.description ? item.service : findServiceInGroup(itemGroup);
  if (service?.description) {
    values.push(props.highlighted(service.description));
  }

  return values.map((v, i) => html`${v}${i < values.length - 1 ? " | " : ""}`);
};

const diagnosticHeaderTemplate = (item: IActivityHistoryItem | undefined) =>
  item &&
  html`
    <div class="diagnostics__header">
      ${item.diagnostics
        .filter((diagnostic: IActivityHistoryDiagnostic) => diagnostic.descripcio != null && diagnostic.descripcio.trim() !== "")
        .map(
          (diagnostic: IActivityHistoryDiagnostic) =>
            html`<dss-badge text=${diagnostic.descripcio?.toUpperCase() as string} state="info" size="sm" outlined hideIcon></dss-badge>`,
        )}
    </div>
  `;
